import { useQuery, UseQueryOptions } from 'react-query'
import { AxiosError } from 'axios'

import { Country } from '../../../constants'
import { useServiceCountryUrl } from '../useServiceCountryUrl'
import { HttpClientConfig, useHttpClientConfig } from '../../useHttpClientConfig'
import axios from 'axios'

type Opportunity = {
  id: string
  salesforceId: string
  deliveryModel: string
  journey: string
  partnerShopGid?: string
  customer?: {
    givenName?: string
    familyName?: string
    timezone?: string
  }
  partnerShopSalesforceId?: string
  fitterSalesforceId?: string
  trialEndDate?: string | null
  // The start of the day *after* trialEndDate, computed by opportunity-service in the customer's own
  // timezone and serialized as a UTC instant (e.g. a trialEndDate of "2026-09-01T00:00:00.000Z" for
  // an Eastern customer becomes "2026-09-02T04:00:00.000Z"). It is therefore an *exclusive* upper
  // bound - `now >= trialExpiration` means the trial is over - and, unlike trialEndDate, it is a
  // real instant rather than a bare date.
  //
  // Computed server-side and therefore genuinely optional - absent when the customer has no timezone
  // on record, when the customer lookup failed, or where the backfill has not run. Consumers must
  // keep a trialEndDate-based fallback (see useTrialGate).
  trialExpiration?: string | null
}

type ApiResponse<T> = { data: T }

const OPPORTUNITY_QUERY_KEY = 'useOpportunities'

export type UseOpportunitiesParams = {
  customerGid?: string
  // The opportunity's own Salesforce ID - use this to fetch one specific, already-known
  // opportunity. Unlike `customerGid`, this is safe to source from a `customer_gid`/`opportunity_gid`
  // URL param regardless of caller: real links into this app pass the *customer's* Salesforce ID as
  // `customer_gid` (not the internal gid), which this endpoint's `customerGid` filter does not
  // match - `opportunity_gid` is always the opportunity's real Salesforce ID, so filtering by it
  // directly avoids that mismatch entirely (see PX-1807).
  salesforceId?: string
  countryCode?: Country
  isCurrent?: boolean
  limit?: number
}

const useOpportunities = (
  { customerGid, salesforceId, countryCode, isCurrent, limit }: UseOpportunitiesParams,
  options?: UseQueryOptions<ApiResponse<Array<Opportunity>>, AxiosError>
) => {
  const { config } = useHttpClientConfig()
  const { url } = useServiceCountryUrl('opportunity')
  const baseURL = `${url.href}/v1/opportunities`

  const fetchOpportunity = async (): Promise<ApiResponse<Array<Opportunity>>> => {
    const response = await axios.get<ApiResponse<Array<Opportunity>>>(baseURL, {
      ...(config as HttpClientConfig),
      params: { customerGid, salesforceId, limit, isCurrent },
    })
    return response.data
  }

  const isEnabledByCaller = options?.enabled ?? true

  const { data, isLoading, isIdle, status, ...rest } = useQuery<ApiResponse<Array<Opportunity>>, AxiosError>(
    // limit/isCurrent/salesforceId are part of the cache key too - different callers (e.g.
    // useResolvedShopGid's unfiltered limit:10 vs useTrialGate's salesforceId-based lookup) must
    // not share a cache entry, or whichever request resolves first silently wins for both.
    [OPPORTUNITY_QUERY_KEY, countryCode, customerGid, salesforceId, limit, isCurrent],
    fetchOpportunity,
    {
      ...options,
      enabled: Boolean(config && isEnabledByCaller),
    }
  )

  // The http config is derived from the access token in an effect, so it is absent for the first
  // render(s) of any cold load and the query cannot start yet. react-query reports a query it has
  // not been allowed to start as idle - isLoading false, no data - and `data` below defaults to [],
  // which together look exactly like "this customer has no opportunity"; callers acted on it, which
  // is what had the trial gate reporting unknown trial data on essentially every load (PX-3765).
  // Report that wait as loading instead, the way the other hooks over this service fold in their own
  // readiness (useAppointmentTypeOptions, useShop, useConsultantUser). Two conditions keep the wait
  // honest: only the caller knows whether it wanted the query at all, so a caller-disabled one still
  // reports settled; and cached data from an earlier mount is already an answer, so a remount that
  // has it does not go back to loading while this hook's own config catches up.
  const isAwaitingHttpConfig = isEnabledByCaller && !config && !data

  return {
    ...rest,
    // Overridden together: `isIdle: true` alongside `isLoading: true` is a state react-query never
    // produces, and a caller reaching for the idiomatic v3 `status`/`isIdle` check would read the
    // gap back as "settled, nothing here" - the exact misreading above.
    status: isAwaitingHttpConfig ? 'loading' : status,
    isIdle: isIdle && !isAwaitingHttpConfig,
    isLoading: isLoading || isAwaitingHttpConfig,
    data: data?.data || [],
  }
}

export { useOpportunities }
